import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../auth/context/AuthContext';
import { supabase } from '../../../shared/lib/supabaseClient';
import { InvestmentAccount } from '../types';
import { ManualTransactionFormData } from '../components/modals/ManualTransactionForm';
import { AddManualAccountFormData } from '../components/modals/AddManualAccountForm';
import { hideItem, unhideItem } from '../services/hiddenItemsApi';
import { normalizeLogoUrl } from '../../../shared/utils/logoHelpers';
import { toast } from 'react-hot-toast';
import { BASE_URL } from '../../../shared/lib/api';

/**
 * useAccountManagement
 * 
 * Custom hook for managing account-related CRUD operations.
 * Handles account loading, creation, updating, and deletion.
 * 
 * Accounts are loaded directly from Supabase for immediate updates.
 */

// Company options for account creation (sorted alphabetically by label, with "Other" always last)
const companyOptions = [
  { value: 'schwab', label: 'Charles Schwab', logo: '/company-schwab-logo.png' },
  { value: 'coinbase', label: 'Coinbase', logo: '/company-coinbase-logo.png' },
  { value: 'etrade', label: 'E*Trade', logo: '/company-etrade-logo.png' },
  { value: 'fidelity', label: 'Fidelity', logo: '/company-fidelity-logo.jpg' },
  { value: 'robinhood', label: 'Robinhood', logo: '/company-robinhood-logo.png' },
  { value: 'vanguard', label: 'Vanguard', logo: '/company-vanguard-logo.png' },
  { value: 'wealthfront', label: 'Wealthfront', logo: '/company-wealthfront-logo.png' },
  { value: 'sofi', label: 'SoFi', logo: '/company-sofi-logo.png' },
  { value: 'tdameritrade', label: 'TD Ameritrade', logo: '/company-tdameritrade-logo.png' },
  { value: 'acorns', label: 'Acorns', logo: '/company-acorns-logo.png' },
  { value: 'tiaa', label: 'TIAA', logo: '/company-tiaa-logo.png' },
  { value: 'other', label: 'Other', logo: '/logo-vester-gray-lrg.png' },
].sort((a, b) => {
  // Always put "Other" at the end
  if (a.value === 'other') return 1;
  if (b.value === 'other') return -1;
  // Sort all others alphabetically
  return a.label.localeCompare(b.label);
});

export interface UseAccountManagementReturn {
  // State
  accounts: InvestmentAccount[];
  accountsError: string | null;
  isDeletingAccount: boolean;

  // Actions
  reloadAccountsFromCache: () => void;
  invalidateTransactions: (accountId?: string) => void; // Helper to invalidate transactions queries
  isLoadingAccounts: boolean;
  handleManualAccountSubmit: (data: AddManualAccountFormData) => Promise<void>;
  handleUpdateAccountName: (accountId: string, newName: string) => Promise<void>;
  handleUpdateAccountValue: (accountId: string, newValue: number) => Promise<void>;
  handleUpdateAccountOrder: (accountIds: string[]) => Promise<void>;
  handleDeleteAccountClick: (accountId: string) => Promise<void>;
  handleToggleAccountHiddenInList: (accountId: string) => Promise<void>;
  handleUpdateAccountHidden: (accountId: string, isHidden: boolean) => Promise<void>;
  handleToggleTransactionHidden: (accountId: string, transactionId: string) => Promise<void>;

  // Utilities
  companyOptions: typeof companyOptions;
}

export const useAccountManagement = (
  userId: string | undefined,
  onAccountCreated?: (accountId: string) => void,
  toggleAccountExpansion?: (accountId: string) => void
): UseAccountManagementReturn => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  // Fetch accounts directly from Supabase
  const {
    data: accounts = [],
    error: accountsError,
    isLoading: isLoadingAccounts,
  } = useQuery<InvestmentAccount[], Error>({
    queryKey: ['accounts', userId],
    queryFn: async (): Promise<InvestmentAccount[]> => {
      if (!userId) {
        return [];
      }

      const { data, error } = await supabase
        .from('accounts')
        .select('*')
        .eq('user_id', userId)
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;
      if (!data) return [];

      // Map database fields to InvestmentAccount format
      return data.map((account) => ({
        id: account.id,
        accountName: account.account_name,
        company: account.company ?? '',
        logoUrl: normalizeLogoUrl(account.logo_url),
        transactions: [], // Transactions loaded separately via useAccountTransactions
        createdAt: account.created_at ?? '',
        isHidden: !!account.is_hidden,
        isManuallyEdited: !!account.is_manually_edited,
        current_value: account.current_value ?? null,
        current_value_updated_at: account.current_value_updated_at ?? null,
        brokerageAccountId: account.brokerage_account_id ?? null,
        brokerageConnectionId: account.brokerage_connection_id ?? null,
        accountStatus: account.account_status ?? null,
        lastSyncTime: account.last_sync_time ?? null,
        isSnapTradeLinked: Boolean(account.brokerage_account_id),
        displayOrder: account.display_order ?? null,
        type: '',
        balance: 0,
        user_id: account.user_id,
      }));
    },
    enabled: !!userId,
    staleTime: 30_000,
  });

  // Helper to invalidate React Query cache after mutations
  const invalidateDashboardCache = useCallback(() => {
    if (user?.id) {
      // Invalidate all dashboard queries for this user
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'self', user.id] });
    }
  }, [queryClient, user?.id]);

  // Helper to invalidate accounts query
  const invalidateAccounts = useCallback(() => {
    if (userId) {
      queryClient.invalidateQueries({ queryKey: ['accounts', userId] });
    }
  }, [queryClient, userId]);

  // Helper to invalidate transactions queries
  const invalidateTransactions = useCallback((accountId?: string) => {
    if (accountId) {
      // Invalidate specific account's transactions
      queryClient.invalidateQueries({ queryKey: ['transactions', accountId] });
    } else {
      // Invalidate all transactions queries
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    }
  }, [queryClient]);

  // Reload accounts from Supabase
  const reloadAccountsFromCache = useCallback(() => {
    invalidateAccounts();
  }, [invalidateAccounts]);

  // Transactions are now fetched via useAccountTransactions hook
  // This helper just invalidates the React Query cache

  // Create new manual account
  const handleManualAccountSubmit = useCallback(
    async (data: AddManualAccountFormData) => {
      if (!userId) {
        console.error('User not logged in, cannot submit account.');
        return;
      }

      const now = new Date().toISOString();
      
      // Get max display_order for this user to place new account at the end
      const { data: maxOrderData } = await supabase
        .from('accounts')
        .select('display_order')
        .eq('user_id', userId)
        .order('display_order', { ascending: false })
        .limit(1);
      
      const maxOrder = maxOrderData && maxOrderData.length > 0 ? maxOrderData[0].display_order : 0;
      const newDisplayOrder = (maxOrder || 0) + 1;
      
      const newDbAccount = {
        user_id: userId,
        account_name: data.accountName.trim(),
        company: data.company,
        logo_url: companyOptions.find((c) => c.value === data.company)?.logo || null,
        is_hidden: data.isHidden ?? false,
        is_manually_edited: true,
        current_value: data.currentValue,
        current_value_updated_at: now,
        display_order: newDisplayOrder,
      };

      try {
        const { data: insertedAccount, error } = await supabase
          .from('accounts')
          .insert(newDbAccount)
          .select()
          .single();

        if (error) throw error;
        if (!insertedAccount) throw new Error('Failed to insert account, no data returned.');

        // Invalidate accounts query to refetch from Supabase (will show new account immediately)
        invalidateAccounts();

        // Fire off recalculation in background (don't wait)
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          const token = sessionData?.session?.access_token;

          if (token) {
            // Fire and forget - don't await
            fetch(`${BASE_URL}/portfolio/recalculate/${insertedAccount.id}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                account_updated: true,
                account_change_type: 'new_account',
                tickers: [],
                div_changed: [],
                opt_changed: [],
                cash_changed: false,
              }),
            }).catch((err) => {
              console.error('Background recalculation failed:', err);
            });
            
            // Invalidate dashboard query to immediately see refresh_needed=true
            // This ensures the toast appears right away (same pattern as transaction CRUD)
            if (userId) {
              queryClient.invalidateQueries({ queryKey: ['dashboard', 'self', userId] });
              // Immediately check rebuild status to start polling if rebuild is in progress
              // This ensures the toast appears right away
              queryClient.refetchQueries({ queryKey: ['private-cache-rebuild-status', userId] });
            }
          }
        } catch (recalcError) {
          console.error('Failed to trigger background recalculation:', recalcError);
        }

        // Notify parent component
        if (onAccountCreated) {
          onAccountCreated(insertedAccount.id);
        }
      } catch (error: any) {
        console.error('Error inserting account:', error);
        alert(`Error adding account: ${error.message}`);
      }
    },
    [userId, invalidateAccounts, onAccountCreated, queryClient]
  );

  // Update account name
  const handleUpdateAccountName = useCallback(
    async (accountId: string, newName: string) => {
      const originalAccount = accounts.find((acc) => acc.id === accountId);
      if (!originalAccount) throw new Error('Account not found.');

      const originalName = originalAccount.accountName;
      if (originalName === newName) return;

      try {
        const { error } = await supabase
          .from('accounts')
          .update({ account_name: newName })
          .eq('id', accountId);

        if (error) throw error;

        // Invalidate accounts query to refetch from Supabase
        invalidateAccounts();

        // Immediately check rebuild status AND refetch dashboard to detect refresh_needed flag
        // This ensures the toast appears right away when either flag is set
        if (userId) {
          queryClient.refetchQueries({ queryKey: ['private-cache-rebuild-status', userId] });
          // Also refetch dashboard to pick up refresh_needed=true flag
          queryClient.refetchQueries({ queryKey: ['dashboard', 'self', userId] });
        }

        // Fire off cache rebuild in background (don't wait)
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          const token = sessionData?.session?.access_token;

          if (token) {
            // Fire and forget - don't await
            fetch(`${BASE_URL}/portfolio/cache/account-info/${accountId}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                account_change_type: 'account_name_changed',
              }),
            }).catch((err) => {
              console.error('Background cache rebuild failed:', err);
            });
          }
        } catch (cacheError) {
          console.error('Failed to trigger background cache rebuild:', cacheError);
        }
      } catch (error: any) {
        console.error(`Error updating account ${accountId} name:`, error);
        throw error;
      }
    },
    [accounts, invalidateAccounts, queryClient, userId]
  );

  // Update account value
  const handleUpdateAccountValue = useCallback(
    async (accountId: string, newValue: number) => {
      const now = new Date().toISOString();

      const { error } = await supabase
        .from('accounts')
        .update({
          current_value: newValue,
          current_value_updated_at: now,
        })
        .eq('id', accountId);

      if (error) throw new Error(`Failed to update account value: ${error.message}`);

      // Invalidate accounts query to refetch from Supabase
      invalidateAccounts();

      // Trigger recalculation via queue so the backend sets the rebuild flag and the toast shows.
      // We must await the recalculate response before refetching status; otherwise we refetch
      // before the request reaches the server and see private_rebuilding=false, so polling never starts.
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;

        if (token) {
          await fetch(`${BASE_URL}/portfolio/recalculate/${accountId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              cash_changed: true,
              tickers: [],
              div_changed: [],
              account_updated: true,
              account_change_type: 'account_value_changed',
            }),
          });
          // Backend has now enqueued and set the rebuild flag; refetch status so the toast appears.
          if (userId) {
            queryClient.refetchQueries({ queryKey: ['private-cache-rebuild-status', userId] });
          }
        }
      } catch (err) {
        console.error('Failed to trigger background recalculation:', err);
      }
    },
    [invalidateAccounts, queryClient, userId]
  );

  // Update account order
  const handleUpdateAccountOrder = useCallback(
    async (accountIds: string[]) => {
      if (!userId || accountIds.length === 0) return;

      try {
        // Update display_order for each account
        const updates = accountIds.map((accountId, index) => 
          supabase
            .from('accounts')
            .update({ display_order: index + 1 })
            .eq('id', accountId)
        );

        // Execute all updates
        const results = await Promise.all(updates);
        
        // Check for errors
        const hasError = results.some(result => result.error);
        if (hasError) {
          const errorResult = results.find(result => result.error);
          throw new Error(`Failed to update account order: ${errorResult?.error?.message}`);
        }

        // Invalidate accounts query to refetch from Supabase
        invalidateAccounts();

        // Immediately check rebuild status AND refetch dashboard to detect refresh_needed flag
        // This ensures the toast appears right away when either flag is set
        if (userId) {
          queryClient.refetchQueries({ queryKey: ['private-cache-rebuild-status', userId] });
          // Also refetch dashboard to pick up refresh_needed=true flag
          queryClient.refetchQueries({ queryKey: ['dashboard', 'self', userId] });
        }

        // Fire off cache rebuild in background (don't wait)
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          const token = sessionData?.session?.access_token;

          if (token) {
            // Fire and forget - don't await
            // Use first account ID as placeholder (cache rebuild applies to all accounts)
            fetch(`${BASE_URL}/portfolio/cache/account-info/${accountIds[0]}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                account_change_type: 'account_order_changed',
              }),
            }).catch((err) => {
              console.error('Background cache rebuild failed:', err);
            });
          }
        } catch (cacheError) {
          console.error('Failed to trigger background cache rebuild:', cacheError);
        }
      } catch (error: any) {
        console.error('Error updating account order:', error);
        throw error;
      }
    },
    [userId, invalidateAccounts, queryClient]
  );

  // Delete account
  const handleDeleteAccountClick = useCallback(
    async (accountId: string) => {
      const accountToDelete = accounts.find((acc) => acc.id === accountId);
      if (!accountToDelete) return;

      // No confirmation here - component handles it
      setIsDeletingAccount(true);

      try {
        const { error: accountError } = await supabase
          .from('accounts')
          .delete()
          .eq('id', accountId);

        if (accountError) throw new Error(`Failed to delete account: ${accountError.message}`);

        // Invalidate accounts query to refetch from Supabase (will remove deleted account)
        invalidateAccounts();

        if (toggleAccountExpansion) {
          toggleAccountExpansion(accountId);
        }

        setIsDeletingAccount(false);

        // Immediately check rebuild status to start polling if rebuild is in progress
        // This ensures the toast appears right away
        if (userId) {
          queryClient.refetchQueries({ queryKey: ['private-cache-rebuild-status', userId] });
        }

        // Trigger portfolio-wide recalculation (fire and forget)
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            fetch(`${BASE_URL}/portfolio/refresh`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${session.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                refresh_type: 'diff_day',
                background: true,  // Run in background with rebuild flag
              }),
            })
              .then(async (response) => {
                if (response.ok) {
                  // Invalidate React Query to trigger refetch from Redis
                  // Backend already rebuilt Redis caches via rebuild_dashboard_caches()
                  invalidateDashboardCache();
                }
              })
              .catch((recalcError) => {
                console.error('Failed to trigger portfolio recalculation:', recalcError);
              });
          }
        } catch (sessionError) {
          console.error('Failed to get session for portfolio recalculation:', sessionError);
        }
      } catch (error: any) {
        setIsDeletingAccount(false);
        console.error(`Error during deletion process for account ${accountId}:`, error);
        toast.error(`Failed to delete account "${accountToDelete.accountName}": ${error.message || error}`);
        throw error;
      }
    },
    [accounts, toggleAccountExpansion, invalidateAccounts, invalidateDashboardCache, queryClient, userId]
  );

  // Toggle account hidden status (from account list)
  const handleToggleAccountHiddenInList = useCallback(
    async (accountId: string) => {
      const account = accounts.find((acc) => acc.id === accountId);
      if (!account) {
        return;
      }

      const currentHiddenState = account.isHidden ?? false;
      const newHiddenState = !currentHiddenState;

      try {
        // Get session token
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !session?.access_token) {
          throw new Error('No auth token');
        }

        // Call API with account-level parameters
        const request = {
          account_id: accountId,
          ticker: '__ALL__',
          event_type: '__ALL__',
          transaction_id: '__ALL__',
        };

        if (newHiddenState) {
          await hideItem(session.access_token, request);
        } else {
          await unhideItem(session.access_token, request);
        }
        
        // Invalidate accounts query to refetch from Supabase
        invalidateAccounts();

        // Immediately check rebuild status to start polling if rebuild is in progress
        // This ensures the toast appears right away
        if (userId) {
          queryClient.refetchQueries({ queryKey: ['private-cache-rebuild-status', userId] });
        }
        
        // Don't invalidate dashboard cache - it will update naturally when background work completes
      } catch (error: any) {
        alert(`Failed to update account visibility: ${error.message || 'Unknown error'}`);
      }
    },
    [accounts, invalidateAccounts, queryClient, userId]
  );

  // Update account hidden status (from edit view)
  const handleUpdateAccountHidden = useCallback(
    async (accountId: string, isHidden: boolean) => {
      try {
        // Get session token
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !session?.access_token) {
          throw new Error('No auth token');
        }

        // Call API with account-level parameters
        const request = {
          account_id: accountId,
          ticker: '__ALL__',
          event_type: '__ALL__',
          transaction_id: '__ALL__',
        };

        if (isHidden) {
          await hideItem(session.access_token, request);
        } else {
          await unhideItem(session.access_token, request);
        }
        
        // Invalidate accounts query to refetch from Supabase
        invalidateAccounts();

        // Immediately check rebuild status to start polling if rebuild is in progress
        // This ensures the toast appears right away
        if (userId) {
          queryClient.refetchQueries({ queryKey: ['private-cache-rebuild-status', userId] });
        }
        
        // Don't invalidate dashboard cache - it will update naturally when background work completes
      } catch (error: any) {
        console.error('Error updating account hidden status:', error);
        alert(`Failed to update account visibility: ${error.message || 'Unknown error'}`);
        throw new Error(`Failed to update account hidden status: ${error.message || 'Unknown error'}`);
      }
    },
    [invalidateAccounts, queryClient, userId]
  );

  // Toggle transaction hidden status
  const handleToggleTransactionHidden = useCallback(
    async (accountId: string, transactionId: string) => {
      // Get transaction from React Query cache to check current state
      const transactionsQuery = queryClient.getQueryData<ManualTransactionFormData[]>(['transactions', accountId]);
      const transaction = transactionsQuery?.find((tx) => tx.id === transactionId);
      if (!transaction) {
        console.warn(`Transaction ${transactionId} not found in cache for account ${accountId}`);
        return;
      }

      const currentHiddenState = transaction.isHidden ?? false;
      const newHiddenState = !currentHiddenState;

      // Optimistic update in React Query cache
      queryClient.setQueryData<ManualTransactionFormData[]>(['transactions', accountId], (old) => {
        if (!old) return old;
        return old.map((t) =>
          t.id === transactionId ? { ...t, isHidden: newHiddenState } : t
        );
      });

      try {
        // Get session token
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !session?.access_token) {
          throw new Error('No auth token');
        }

        // Determine transaction type and build request
        const equityOptionTypes = ['Buy', 'Sell', 'Short Sell', 'Buy to Cover', 'Call', 'Call Sell', 'Put', 'Put Sell'];
        
        let request: {
          account_id: string;
          ticker?: string | null;
          event_type?: string | null;
          transaction_id?: string | null;
        };

        if (equityOptionTypes.includes(transaction.type || '')) {
          // Equity/Option transaction - hide/unhide by ticker
          request = {
            account_id: accountId,
            ticker: transaction.ticker || null,
            event_type: '__ALL__',
            transaction_id: '__ALL__',
          };
        } else if (transaction.type === 'Interest') {
          // Interest transaction - hide/unhide just this one transaction
          request = {
            account_id: accountId,
            ticker: null,
            event_type: 'INTEREST',
            transaction_id: transactionId,
          };
        } else {
          // Cash transaction or other types - hide/unhide specific transaction
          // Map transaction type to cash event enum
          const typeUpper = (transaction.type || '').toUpperCase();
          
          if (typeUpper === 'DEPOSIT' || typeUpper === 'WITHDRAWAL' || typeUpper === 'ADJUSTMENT') {
            // Pure cash transaction - hide/unhide specific transaction
            request = {
              account_id: accountId,
              ticker: null,
              event_type: typeUpper,
              transaction_id: transactionId,
            };
          } else if (typeUpper.includes('DIVIDEND')) {
            // Dividends are not pure cash transactions (they have tickers), treat as equity
            request = {
              account_id: accountId,
              ticker: transaction.ticker || null,
              event_type: '__ALL__',
              transaction_id: '__ALL__',
            };
          } else {
            throw new Error(`Unknown transaction type: ${transaction.type}`);
          }
        }

        if (newHiddenState) {
          await hideItem(session.access_token, request);
        } else {
          await unhideItem(session.access_token, request);
        }
        
        // Immediately check rebuild status to start polling if rebuild is in progress
        // This ensures the toast appears right away
        if (userId) {
          queryClient.refetchQueries({ queryKey: ['private-cache-rebuild-status', userId] });
        }
        
        // Invalidate transactions query to refetch with updated is_hidden values
        // This is important because hiding a ticker updates ALL transactions with that ticker
        invalidateTransactions(accountId);
        
        // Don't invalidate dashboard cache - it will update naturally when background work completes
      } catch (error: any) {
        console.error('Error updating transaction hidden state:', error);
        // Revert optimistic update in React Query cache
        queryClient.setQueryData<ManualTransactionFormData[]>(['transactions', accountId], (old) => {
          if (!old) return old;
          return old.map((t) =>
            t.id === transactionId ? { ...t, isHidden: currentHiddenState } : t
          );
        });
        alert(`Failed to update transaction visibility: ${error.message || 'Unknown error'}`);
      }
    },
    [queryClient, invalidateTransactions, userId]
  );

  return {
    // State
    accounts,
    accountsError: accountsError?.message ?? null,
    isDeletingAccount,
    isLoadingAccounts,

    // Actions
    reloadAccountsFromCache,
    invalidateTransactions,
    handleManualAccountSubmit,
    handleUpdateAccountName,
    handleUpdateAccountValue,
    handleUpdateAccountOrder,
    handleDeleteAccountClick,
    handleToggleAccountHiddenInList,
    handleUpdateAccountHidden,
    handleToggleTransactionHidden,

    // Utilities
    companyOptions,
  };
};
